interface OfflineUpdateRecord {
  id: string;
  docId: string;
  iv: string;
  data: string;
  timestamp: number;
}

interface DocStateRecord {
  docId: string;
  iv: string;
  data: string;
  sv: string;
  timestamp: number;
}

export interface OfflineStorageStats {
  recordCount: number;
  estimatedBytes: number;
  quotaBytes: number;
  usageRatio: number;
}

export class OfflineCryptoStorage {
  private db: IDBDatabase | null = null;
  private cryptoKey: CryptoKey | null = null;

  private static DB_NAME = 'memo_offline_crdt';
  private static DB_VERSION = 3;
  private static STORE_NAME = 'offline_updates';
  private static DOC_STATE_STORE_NAME = 'doc_states';
  private static SALT_STORE_NAME = 'key_salts';
  private static PBKDF2_ITERATIONS = 100000;
  private static SESSION_KEY_STORAGE = 'memo_session_key';
  private static MAX_OFFLINE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  private static MAX_RECORDS_PER_DOC = 500;
  private static MAX_UPDATE_SIZE_BYTES = 256 * 1024;
  private static STORAGE_QUOTA_WARN_RATIO = 0.8;

  private initPromise: Promise<void> | null = null;
  private initialized = false;
  private recordCounts: Map<string, number> = new Map();
  private lastQuotaWarning = false;

  async init(userIdentifier: string): Promise<void> {
    if (this.initialized && this.cryptoKey) return;

    if (this.initPromise) return this.initPromise;

    this.initPromise = this._init(userIdentifier);
    try {
      await this.initPromise;
    } catch (e) {
      this.initPromise = null;
      throw e;
    }
  }

  private async _init(userIdentifier: string): Promise<void> {
    await this.openDB();
    await this.deriveKeyFromSession(userIdentifier);
    await this.loadRecordCounts();
    this.initialized = true;
  }

  private openDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(OfflineCryptoStorage.DB_NAME, OfflineCryptoStorage.DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(OfflineCryptoStorage.STORE_NAME)) {
          const store = db.createObjectStore(OfflineCryptoStorage.STORE_NAME, { keyPath: 'id' });
          store.createIndex('docId', 'docId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        if (!db.objectStoreNames.contains(OfflineCryptoStorage.DOC_STATE_STORE_NAME)) {
          db.createObjectStore(OfflineCryptoStorage.DOC_STATE_STORE_NAME, { keyPath: 'docId' });
        }
        if (!db.objectStoreNames.contains(OfflineCryptoStorage.SALT_STORE_NAME)) {
          db.createObjectStore(OfflineCryptoStorage.SALT_STORE_NAME, { keyPath: 'userId' });
        }
      };

      request.onblocked = () => {
        console.warn('[OfflineCryptoStorage] DB upgrade blocked by another tab');
      };

      request.onsuccess = () => {
        this.db = request.result;

        this.db.onversionchange = () => {
          console.warn('[OfflineCryptoStorage] DB version change requested, closing connection');
          this.db?.close();
          this.db = null;
          this.initialized = false;
        };

        resolve();
      };

      request.onerror = () => reject(request.error);
    });
  }

  private async loadRecordCounts(): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve) => {
      const tx = this.db!.transaction(OfflineCryptoStorage.STORE_NAME, 'readonly');
      const store = tx.objectStore(OfflineCryptoStorage.STORE_NAME);
      const index = store.index('docId');
      const request = index.openCursor();
      const counts = new Map<string, number>();

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const docId = cursor.value.docId as string;
          counts.set(docId, (counts.get(docId) || 0) + 1);
          cursor.continue();
        } else {
          this.recordCounts = counts;
          resolve();
        }
      };

      request.onerror = () => {
        this.recordCounts = new Map();
        resolve();
      };
    });
  }

  private async getUserSalt(userId: string): Promise<Uint8Array> {
    if (!this.db) throw new Error('DB not initialized');

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(OfflineCryptoStorage.SALT_STORE_NAME, 'readwrite');
      const store = tx.objectStore(OfflineCryptoStorage.SALT_STORE_NAME);
      const getReq = store.get(userId);

      getReq.onsuccess = () => {
        if (getReq.result) {
          const saltBase64 = getReq.result.salt as string;
          resolve(this.base64ToUint8Array(saltBase64));
        } else {
          const salt = crypto.getRandomValues(new Uint8Array(32));
          store.put({ userId, salt: this.uint8ArrayToBase64(salt) });
          resolve(salt);
        }
      };

      getReq.onerror = () => reject(getReq.error);
    });
  }

  private async deriveKeyFromSession(userIdentifier: string): Promise<void> {
    const cachedKey = sessionStorage.getItem(OfflineCryptoStorage.SESSION_KEY_STORAGE);
    if (cachedKey) {
      try {
        const rawKey = this.base64ToUint8Array(cachedKey);
        this.cryptoKey = await crypto.subtle.importKey(
          'raw',
          rawKey,
          { name: 'AES-GCM', length: 256 },
          false,
          ['encrypt', 'decrypt']
        );
        return;
      } catch {
        sessionStorage.removeItem(OfflineCryptoStorage.SESSION_KEY_STORAGE);
      }
    }

    const encoder = new TextEncoder();
    const userId = userIdentifier || 'anonymous';
    const keyMaterialInput = `wise-memo-offline:${userId}`;

    const userSalt = await this.getUserSalt(userId);

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(keyMaterialInput),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    this.cryptoKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: userSalt,
        iterations: OfflineCryptoStorage.PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );

    const exported = await crypto.subtle.exportKey('raw', this.cryptoKey);
    sessionStorage.setItem(
      OfflineCryptoStorage.SESSION_KEY_STORAGE,
      this.uint8ArrayToBase64(new Uint8Array(exported))
    );
  }

  async saveOfflineUpdate(docId: string, update: Uint8Array): Promise<boolean> {
    if (!this.cryptoKey || !this.db) return false;

    if (update.byteLength > OfflineCryptoStorage.MAX_UPDATE_SIZE_BYTES) {
      console.warn(
        `[OfflineCryptoStorage] Update too large (${update.byteLength} bytes), max ${OfflineCryptoStorage.MAX_UPDATE_SIZE_BYTES} bytes, skipping`
      );
      return false;
    }

    try {
      const currentCount = this.recordCounts.get(docId) || 0;
      if (currentCount >= OfflineCryptoStorage.MAX_RECORDS_PER_DOC) {
        await this.evictOldestRecords(docId, currentCount - OfflineCryptoStorage.MAX_RECORDS_PER_DOC + 1);
      }

      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        this.cryptoKey,
        update
      );

      const record: OfflineUpdateRecord = {
        id: `${docId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        docId,
        iv: this.uint8ArrayToBase64(iv),
        data: this.uint8ArrayToBase64(new Uint8Array(encrypted)),
        timestamp: Date.now(),
      };

      await this.writeRecord(record);
      this.recordCounts.set(docId, (this.recordCounts.get(docId) || 0) + 1);
      this.checkQuotaWarning();
      return true;
    } catch (e) {
      console.error('[OfflineCryptoStorage] saveOfflineUpdate failed:', e);
      return false;
    }
  }

  async loadOfflineUpdates(docId: string): Promise<Uint8Array[]> {
    if (!this.cryptoKey || !this.db) return [];

    const records = await this.readRecordsByDocId(docId);
    const updates: Uint8Array[] = [];
    const now = Date.now();
    const expiredIds: string[] = [];

    for (const record of records) {
      if (now - record.timestamp > OfflineCryptoStorage.MAX_OFFLINE_AGE_MS) {
        expiredIds.push(record.id);
        continue;
      }

      try {
        const iv = this.base64ToUint8Array(record.iv);
        const data = this.base64ToUint8Array(record.data);
        const decrypted = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv },
          this.cryptoKey!,
          data
        );
        updates.push(new Uint8Array(decrypted));
      } catch {
        expiredIds.push(record.id);
      }
    }

    if (expiredIds.length > 0) {
      await this.deleteRecords(expiredIds);
      this.recordCounts.set(docId, Math.max(0, (this.recordCounts.get(docId) || 0) - expiredIds.length));
    }

    return updates;
  }

  async hasPersistedUpdates(docId: string): Promise<boolean> {
    if (!this.db) return false;
    const count = await this.countRecordsFromDB(docId);
    return count > 0;
  }

  async clearDocUpdates(docId: string): Promise<void> {
    if (!this.db) return;

    const records = await this.readRecordsByDocId(docId);
    const ids = records.map(r => r.id);
    if (ids.length > 0) {
      await this.deleteRecords(ids);
    }
    this.recordCounts.delete(docId);
  }

  async saveDocState(docId: string, update: Uint8Array, sv: Uint8Array): Promise<boolean> {
    if (!this.cryptoKey || !this.db) return false;

    try {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        this.cryptoKey,
        update
      );

      const svIv = crypto.getRandomValues(new Uint8Array(12));
      const svEncrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: svIv },
        this.cryptoKey,
        sv
      );

      const record: DocStateRecord = {
        docId,
        iv: this.uint8ArrayToBase64(iv),
        data: this.uint8ArrayToBase64(new Uint8Array(encrypted)),
        sv: this.uint8ArrayToBase64(svIv) + '.' + this.uint8ArrayToBase64(new Uint8Array(svEncrypted)),
        timestamp: Date.now(),
      };

      await this.writeDocStateRecord(record);
      return true;
    } catch (e) {
      console.error('[OfflineCryptoStorage] saveDocState failed:', e);
      return false;
    }
  }

  async loadDocState(docId: string): Promise<{ update: Uint8Array; sv: Uint8Array } | null> {
    if (!this.cryptoKey || !this.db) return null;

    try {
      const record = await this.readDocStateRecord(docId);
      if (!record) return null;

      const iv = this.base64ToUint8Array(record.iv);
      const data = this.base64ToUint8Array(record.data);
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        this.cryptoKey,
        data
      );

      const svParts = record.sv.split('.');
      const svIv = this.base64ToUint8Array(svParts[0]);
      const svData = this.base64ToUint8Array(svParts[1]);
      const svDecrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: svIv },
        this.cryptoKey,
        svData
      );

      return {
        update: new Uint8Array(decrypted),
        sv: new Uint8Array(svDecrypted),
      };
    } catch (e) {
      console.warn('[OfflineCryptoStorage] loadDocState failed, clearing corrupt record:', e);
      try {
        await this.deleteDocStateRecord(docId);
      } catch { /* ignore */ }
      return null;
    }
  }

  async clearDocState(docId: string): Promise<void> {
    if (!this.db) return;
    await this.deleteDocStateRecord(docId);
  }

  async clearAll(): Promise<void> {
    this.cryptoKey = null;
    sessionStorage.removeItem(OfflineCryptoStorage.SESSION_KEY_STORAGE);
    this.recordCounts.clear();

    if (this.db) {
      try {
        const tx = this.db.transaction(
          [OfflineCryptoStorage.STORE_NAME, OfflineCryptoStorage.DOC_STATE_STORE_NAME, OfflineCryptoStorage.SALT_STORE_NAME],
          'readwrite'
        );
        tx.objectStore(OfflineCryptoStorage.STORE_NAME).clear();
        tx.objectStore(OfflineCryptoStorage.DOC_STATE_STORE_NAME).clear();
        tx.objectStore(OfflineCryptoStorage.SALT_STORE_NAME).clear();
      } catch {
        // ignore
      }

      try {
        this.db.close();
      } catch {
        // ignore
      }
      this.db = null;
    }

    this.initialized = false;
    this.initPromise = null;
  }

  async getStorageStats(): Promise<OfflineStorageStats> {
    let recordCount = 0;
    if (this.db) {
      return new Promise((resolve) => {
        const tx = this.db!.transaction(OfflineCryptoStorage.STORE_NAME, 'readonly');
        const store = tx.objectStore(OfflineCryptoStorage.STORE_NAME);
        const countReq = store.count();
        countReq.onsuccess = () => {
          recordCount = countReq.result;
          this.queryStorageQuota(recordCount, resolve);
        };
        countReq.onerror = () => {
          this.queryStorageQuota(0, resolve);
        };
      });
    }

    return { recordCount: 0, estimatedBytes: 0, quotaBytes: 0, usageRatio: 0 };
  }

  private queryStorageQuota(recordCount: number, resolve: (stats: OfflineStorageStats) => void): void {
    if (navigator.storage && navigator.storage.estimate) {
      navigator.storage.estimate().then((estimate) => {
        const quotaBytes = estimate.quota || 0;
        const usageBytes = estimate.usage || 0;
        resolve({
          recordCount,
          estimatedBytes: usageBytes,
          quotaBytes,
          usageRatio: quotaBytes > 0 ? usageBytes / quotaBytes : 0,
        });
      }).catch(() => {
        resolve({ recordCount, estimatedBytes: 0, quotaBytes: 0, usageRatio: 0 });
      });
    } else {
      resolve({ recordCount, estimatedBytes: 0, quotaBytes: 0, usageRatio: 0 });
    }
  }

  isInitialized(): boolean {
    return this.initialized && this.cryptoKey !== null;
  }

  isStorageQuotaWarning(): boolean {
    return this.lastQuotaWarning;
  }

  private checkQuotaWarning(): void {
    this.getStorageStats().then(stats => {
      this.lastQuotaWarning = stats.quotaBytes > 0 && stats.usageRatio >= OfflineCryptoStorage.STORAGE_QUOTA_WARN_RATIO;
    }).catch(() => {
      this.lastQuotaWarning = false;
    });
  }

  private writeRecord(record: OfflineUpdateRecord): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) { resolve(); return; }
      const tx = this.db.transaction(OfflineCryptoStorage.STORE_NAME, 'readwrite');
      tx.objectStore(OfflineCryptoStorage.STORE_NAME).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
    });
  }

  private readRecordsByDocId(docId: string): Promise<OfflineUpdateRecord[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) { resolve([]); return; }
      const tx = this.db.transaction(OfflineCryptoStorage.STORE_NAME, 'readonly');
      const index = tx.objectStore(OfflineCryptoStorage.STORE_NAME).index('docId');
      const request = index.getAll(docId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  private deleteRecords(ids: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db || ids.length === 0) { resolve(); return; }
      const tx = this.db.transaction(OfflineCryptoStorage.STORE_NAME, 'readwrite');
      const store = tx.objectStore(OfflineCryptoStorage.STORE_NAME);
      for (const id of ids) {
        store.delete(id);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
    });
  }

  private countRecordsFromDB(docId: string): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!this.db) { resolve(0); return; }
      const tx = this.db.transaction(OfflineCryptoStorage.STORE_NAME, 'readonly');
      const index = tx.objectStore(OfflineCryptoStorage.STORE_NAME).index('docId');
      const request = index.count(docId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private evictOldestRecords(docId: string, count: number): Promise<void> {
    if (!this.db || count <= 0) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(OfflineCryptoStorage.STORE_NAME, 'readwrite');
      const store = tx.objectStore(OfflineCryptoStorage.STORE_NAME);
      const index = store.index('docId');
      const keyRange = IDBKeyRange.only(docId);
      const cursorReq = index.openCursor(keyRange, 'next');

      let evicted = 0;
      const idsToDelete: string[] = [];

      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor && evicted < count) {
          idsToDelete.push(cursor.value.id);
          evicted++;
          cursor.continue();
        } else {
          const deleteTx = this.db!.transaction(OfflineCryptoStorage.STORE_NAME, 'readwrite');
          const deleteStore = deleteTx.objectStore(OfflineCryptoStorage.STORE_NAME);
          for (const id of idsToDelete) {
            deleteStore.delete(id);
          }
          deleteTx.oncomplete = () => {
            this.recordCounts.set(docId, Math.max(0, (this.recordCounts.get(docId) || 0) - evicted));
            resolve();
          };
          deleteTx.onerror = () => reject(deleteTx.error);
          deleteTx.onabort = () => reject(deleteTx.error || new Error('Transaction aborted'));
        }
      };

      cursorReq.onerror = () => reject(cursorReq.error);
    });
  }

  private uint8ArrayToBase64(bytes: Uint8Array): string {
    const chunks: string[] = [];
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const end = Math.min(i + chunkSize, bytes.length);
      let chunk = '';
      for (let j = i; j < end; j++) {
        chunk += String.fromCharCode(bytes[j]);
      }
      chunks.push(chunk);
    }
    return btoa(chunks.join(''));
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private writeDocStateRecord(record: DocStateRecord): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) { resolve(); return; }
      const tx = this.db.transaction(OfflineCryptoStorage.DOC_STATE_STORE_NAME, 'readwrite');
      tx.objectStore(OfflineCryptoStorage.DOC_STATE_STORE_NAME).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
    });
  }

  private readDocStateRecord(docId: string): Promise<DocStateRecord | null> {
    return new Promise((resolve, reject) => {
      if (!this.db) { resolve(null); return; }
      const tx = this.db.transaction(OfflineCryptoStorage.DOC_STATE_STORE_NAME, 'readonly');
      const request = tx.objectStore(OfflineCryptoStorage.DOC_STATE_STORE_NAME).get(docId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  private deleteDocStateRecord(docId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) { resolve(); return; }
      const tx = this.db.transaction(OfflineCryptoStorage.DOC_STATE_STORE_NAME, 'readwrite');
      tx.objectStore(OfflineCryptoStorage.DOC_STATE_STORE_NAME).delete(docId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

export const offlineCryptoStorage = new OfflineCryptoStorage();
